import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  List,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Toolbar,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import { SelfHostedEditor } from "../../../components/TextEditor";
import { ImageCropField } from "../../../components/ImageCropField";
import { useImageCrop } from "../../../hooks";
import { ApiPost, ApiPut } from "../../../helpers/API/ApiData";
import { getUploadErrorMessage } from "../../../helpers/uploadErrorMessage";
import { DateSection } from "./DateSection";

export const defaultInputValue = {
  newsName: "",
  newsName_ger: "",
  newsDescription: "",
  newsDescription_ger: "",
  date: "",
  file: "",
  file_ger: "",
};

type Props = {
  isOpen: boolean;
  loading: boolean;
  isEditing: boolean;
  editData: typeof defaultInputValue;
  idForNewsEdit: string;
  onClose: () => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getNewsDetails: () => void;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  handleOnInputFieldChange: (e: any) => void;
};

export const NewsForm = ({
  isOpen,
  loading,
  isEditing,
  editData,
  idForNewsEdit,
  onClose,
  setLoading,
  setModalOpen,
  getNewsDetails,
  setEditing,
}: Props) => {
  const [inputValue, setInputValue] = useState(editData || defaultInputValue);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (editData) {
      setInputValue(editData);
    }
  }, [editData]);

  const imageCropEn = useImageCrop();
  const imageCropGer = useImageCrop();
  const [removeImageEn, setRemoveImageEn] = useState(false);
  const [removeImageGer, setRemoveImageGer] = useState(false);

  const handleOnChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({ ...inputValue, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleClose = () => {
    imageCropEn.reset();
    imageCropGer.reset();
    setRemoveImageEn(false);
    setRemoveImageGer(false);
    setErrors({});
    onClose();
  };

  const formValidation = () => {
    let formIsValid = true;
    const err: any = {};

    if (!inputValue?.newsName?.trim()) {
      formIsValid = false;
      err["newsName"] = "*Bitte geben Sie den Namen ein";
    }
    if (!inputValue?.newsName_ger?.trim()) {
      formIsValid = false;
      err["newsName_ger"] = "*Bitte geben Sie den Namen ein";
    }
    if (!inputValue?.date) {
      formIsValid = false;
      err["date"] = "*Bitte wählen Sie ein Datum aus";
    }
    if (!imageCropEn.imageToUpload && !inputValue?.file) {
      formIsValid = false;
      err["file"] = "*Bitte wählen Sie ein Bild aus";
    }
    if (!imageCropGer.imageToUpload && !inputValue?.file_ger) {
      formIsValid = false;
      err["file_ger"] = "*Bitte wählen Sie ein Bild aus";
    }

    setErrors(err);
    return formIsValid;
  };

  const handleSave = async () => {
    if (!formValidation()) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("newsName", inputValue?.newsName);
      formData.append("newsName_ger", inputValue?.newsName_ger);
      formData.append("newsDescription", inputValue?.newsDescription);
      formData.append("newsDescription_ger", inputValue?.newsDescription_ger);
      formData.append("date", inputValue?.date);

      const croppedEn = await imageCropEn.getCroppedFile();
      if (croppedEn) formData.append("mediaUrl", croppedEn);
      const croppedGer = await imageCropGer.getCroppedFile();
      if (croppedGer) formData.append("mediaUrl_ger", croppedGer);
      if (isEditing && removeImageEn) formData.append("removeMediaUrl", "true");
      if (isEditing && removeImageGer) formData.append("removeMediaUrl_ger", "true");

      if (isEditing) {
        await ApiPut(`news/update?id=${idForNewsEdit}`, formData);
        setEditing(false);
        toast.success("Erfolgreich aktualisiert!");
      } else {
        await ApiPost(`news/create`, formData);
        toast.success("Fügten erfolgreich hinzu!");
      }

      setModalOpen(false);
      imageCropEn.reset();
      imageCropGer.reset();
      setRemoveImageEn(false);
      setRemoveImageGer(false);
      setInputValue(defaultInputValue);
      getNewsDetails();
    } catch (err: any) {
      toast.error(getUploadErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullScreen>
      <Toolbar>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <List>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Deutsch
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                English
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Name</Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                id="newsName_ger"
                name="newsName_ger"
                value={inputValue?.newsName_ger}
                onChange={handleOnChange}
              />
              <Typography variant="caption" color="error">
                {errors["newsName_ger"]}
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                id="newsName"
                name="newsName"
                value={inputValue?.newsName}
                onChange={handleOnChange}
              />
              <Typography variant="caption" color="error">
                {errors["newsName"]}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
              Beschreibung
            </Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <SelfHostedEditor
                onChange={(_event: any, editor: any) => {
                  setInputValue((prev) => ({
                    ...prev,
                    newsDescription_ger: editor?.getContent(),
                  }));
                }}
                initialValue={inputValue?.newsDescription_ger}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <SelfHostedEditor
                onChange={(_event: any, editor: any) => {
                  setInputValue((prev) => ({
                    ...prev,
                    newsDescription: editor?.getContent(),
                  }));
                }}
                initialValue={inputValue?.newsDescription}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
              Datum auswählen
            </Typography>
            <Box sx={{ width: { xs: "100%", lg: "83.33%" } }}>
              <TextField
                type="date"
                size="small"
                fullWidth
                variant="outlined"
                id="date"
                name="date"
                value={inputValue?.date}
                onChange={handleOnChange}
                InputLabelProps={{ shrink: true }}
              />
              <Typography variant="caption" color="error">
                {errors["date"]}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Bild</Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <ImageCropField
                inputId="mediaUrl_ger"
                imageCrop={imageCropGer}
                existingImage={typeof inputValue?.file_ger === "string" ? inputValue?.file_ger : undefined}
                error={errors["file_ger"]}
                onRemoveExistingImage={() => {
                  setInputValue((prev) => ({ ...prev, file_ger: "" }));
                  setRemoveImageGer(true);
                }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <ImageCropField
                inputId="imageUpload"
                imageCrop={imageCropEn}
                existingImage={typeof inputValue?.file === "string" ? inputValue?.file : undefined}
                error={errors["file"]}
                onRemoveExistingImage={() => {
                  setInputValue((prev) => ({ ...prev, file: "" }));
                  setRemoveImageEn(true);
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {loading ? (
            <Button variant="contained" disabled>
              {isEditing ? "Speichern" : "Hinzufügen"}
              <CircularProgress size={20} sx={{ ml: 2 }} />
            </Button>
          ) : (
            <Button variant="contained" sx={{ mr: 2 }} onClick={handleSave}>
              {isEditing ? "Speichern" : "Hinzufügen"}
            </Button>
          )}
        </Box>
      </List>
    </Dialog>
  );
};
